import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Column, InputSelectField, Typography,
} from '@tempmee/react';
import { FeatureFlagKeys } from '../../../../constants/ff.posthog';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import useAccessibleName from '../../../../hooks/useAccessibleName';
import { ProfileTabBody } from '../components/ProfileTabBody';
import { usePromotions } from './hooks';
import { BestInDentalCycleHistory } from './BestInDentalCycleHistory';
import { ProfessionalPromotionPayouts } from './ProfessionalPromotionPayouts';

/**
 * The MVP single-status control, kept as the flag-off fallback: it edits the
 * monolith's one bestInDental field, which is all there is to manage until the
 * multi-cycle admin (gated with the rest of the Best in Dental admin panel)
 * is rolled out.
 */
const LegacyContestStatus = () => {
  const { options, formValue, handleStatusChanged } = usePromotions();
  const selectRef = useAccessibleName('Contest Status');

  return (
    <Column align="center" gap={4} py={6}>
      <Typography variant="h5" as="h2">Best in Dental</Typography>
      <Box width="320px">
        <InputSelectField
          ref={selectRef}
          label="Contest Status"
          options={options}
          value={formValue}
          onChange={handleStatusChanged}
        />
      </Box>
    </Column>
  );
};

export const PromotionsTabScreen = () => {
  const userId = useSelector((state) => state.users.currentUser?.id);

  // Reactive, not the synchronous getShouldEnableBestInDental() read: PostHog
  // fetches flags asynchronously (defaulting false), so a one-shot read here
  // would leave the legacy editor on screen for a flag-on admin who reached
  // the tab before the flags arrived. The synchronous idiom is for App.js
  // route registration only.
  const isMultiCycleEnabled = useFeatureFlag(
    FeatureFlagKeys.bestInDentalAdminPanel,
    false,
  );

  // Flag off and user-not-loaded are different states with different
  // fallbacks: only the former should offer the legacy editor (whose mutation
  // dereferences currentUser). With the flag on and no user yet, render
  // nothing — the profile page is still loading or failed to load the user.
  // The legacy fallback spaces itself (py={6}); the history card needs the
  // same breathing room from the tab bar above it.
  const bestInDentalSection = () => {
    if (!isMultiCycleEnabled) return <LegacyContestStatus />;
    if (!userId) return null;

    return (
      <ProfileTabBody>
        <BestInDentalCycleHistory userId={userId} />
      </ProfileTabBody>
    );
  };

  return (
    <>
      {bestInDentalSection()}

      <ProfessionalPromotionPayouts />
    </>
  );
};
